/**
 * Monte Carlo CTR Simulator v3
 *
 * Generates 100,000 synthetic viewer profiles with randomized traits,
 * then simulates whether each profile would click on a given title.
 *
 * Architecture:
 *   1. Title signals → "title quality" score (0-1) — how compelling is this title?
 *   2. Viewer profile → "receptiveness" score — how likely is this viewer to click anything?
 *   3. Signal-trait matching → interaction multiplier — does this title appeal to this viewer?
 *   4. Context layer → environmental noise (mood, scroll, fatigue, competition)
 *   5. Chaos events → rare micro-events that break the pattern
 *
 * Key design: title quality is MULTIPLICATIVE, not additive. A title with
 * score 0.8 produces ~4x the CTR of a title with score 0.2.
 */

// ============================================================
//  SIGNAL DICTIONARIES
// ============================================================

const POWER_WORDS = new Set([
  'secret', 'hack', 'trick', 'mistake', 'warning', 'banned', 'exposed',
  'shocking', 'insane', 'unbelievable', 'incredible', 'amazing', 'ultimate',
  'best', 'worst', 'never', 'always', 'free', 'guaranteed', 'proven',
  'dangerous', 'deadly', 'urgent', 'breaking', 'revealed', 'hidden',
  'truth', 'lie', 'wrong', 'ruined', 'destroyed', 'changed', 'forever',
  'stop', 'must', 'need', 'perfect', 'impossible', 'genius', 'illegal',
  'controversial', 'rare', 'exclusive', 'epic', 'massive', 'tiny',
]);

const EMOTIONAL_WORDS = new Set([
  'love', 'hate', 'fear', 'cry', 'laugh', 'angry', 'sad', 'happy',
  'terrifying', 'heartbreaking', 'hilarious', 'beautiful', 'ugly',
  'painful', 'satisfying', 'disgusting', 'embarrassing', 'inspiring',
  'emotional', 'devastating', 'wholesome', 'cringe', 'awkward',
  'obsessed', 'furious', 'thrilled', 'shocked', 'jealous',
  'regret', 'grateful', 'scared', 'excited', 'miserable',
]);

const URGENCY_WORDS = new Set([
  'now', 'hurry', 'limited', 'last', 'ending', 'final',
  'before', 'deadline', 'immediately', 'asap', 'quick',
  'running out', 'don\'t miss', 'act fast',
]);

const PERSONAL_PRONOUNS = new Set([
  'i', 'my', 'me', 'you', 'your', 'we', 'our',
]);

const CURIOSITY_PATTERNS = [
  { pattern: /\?$/, weight: 1.2 },
  { pattern: /^why\b.{10,}/i, weight: 1.0 },           // "Why X" with substance
  { pattern: /^how I\b/i, weight: 0.9 },                // "How I" = personal how-to
  { pattern: /^how to .{15,}/i, weight: 0.7 },          // "How to X" only with detail
  { pattern: /^what happens/i, weight: 0.9 },
  { pattern: /^what if\b/i, weight: 1.0 },
  { pattern: /^what would/i, weight: 0.9 },
  { pattern: /^what'?s inside/i, weight: 0.9 },
  { pattern: /you won't believe/i, weight: 1.5 },
  { pattern: /nobody knows/i, weight: 1.3 },
  { pattern: /the real reason/i, weight: 1.4 },
  { pattern: /the truth about/i, weight: 1.3 },
  { pattern: /here'?s why/i, weight: 1.0 },
  { pattern: /this is why/i, weight: 1.0 },
  { pattern: /i was wrong/i, weight: 1.4 },
  { pattern: /finally revealed/i, weight: 1.2 },
  { pattern: /you need to (know|see|hear)/i, weight: 1.1 },
  { pattern: /don't.*until/i, weight: 1.3 },
  { pattern: /before you/i, weight: 0.9 },
  { pattern: /they don't want you/i, weight: 1.5 },
  { pattern: /no one is talking/i, weight: 1.4 },
  { pattern: /everything wrong/i, weight: 1.1 },
  { pattern: /is it worth/i, weight: 0.8 },
  { pattern: /I tried/i, weight: 1.0 },
  { pattern: /I tested/i, weight: 1.1 },
  { pattern: /went wrong/i, weight: 1.2 },
  { pattern: /not what (I|you|we) expected/i, weight: 1.3 },
  { pattern: /the \w+ no one talks about/i, weight: 1.4 },
  { pattern: /\.\.\.$/, weight: 0.7 },
  { pattern: /would you\b/i, weight: 0.9 },
  { pattern: /can you\b.{10,}/i, weight: 0.8 },
  { pattern: /is .+ (actually|really|still)/i, weight: 0.8 },
  { pattern: /why no one (has|can|will|does)/i, weight: 1.3 },
  { pattern: /no one (has ever|can|will ever)/i, weight: 1.2 },
  { pattern: /(the|math'?s|science'?s) .{3,} (flaw|paradox|problem|mystery|secret)/i, weight: 1.1 },
  { pattern: /how .{3,} (invented|discovered|created|changed)/i, weight: 0.9 },
  // Mystery structures that create knowledge gaps
  { pattern: /shouldn't (exist|be alive|work|be possible|be real)/i, weight: 1.3 },
  { pattern: /can't be (opened|explained|solved|measured|seen)/i, weight: 1.2 },
  { pattern: /the .{3,} that (changes|predicts|turns|moves|breaks|defies|bends)/i, weight: 1.1 },
  { pattern: /the .{3,} (no one|nobody) (can|knows|understands|has)/i, weight: 1.3 },
  { pattern: /the (real|true|actual|scientific|bizarre|dark) (cost|reason|side|behavior|truth|story)/i, weight: 1.0 },
  { pattern: /actually (works?|happened|true|real|exists?)/i, weight: 0.8 },
  { pattern: /found a .{3,} (under|inside|behind|beneath)/i, weight: 1.1 },
];

// ============================================================
//  TITLE ANALYSIS — produces a quality score 0 to 1
// ============================================================

function analyzeTitle(title) {
  const lower = title.toLowerCase();
  const words = lower.split(/\s+/);
  const cleanWords = words.map(w => w.replace(/[^a-z']/g, '')).filter(Boolean);
  const wordCount = cleanWords.length;

  // --- Individual signals (each 0 to 1) ---

  // Power words — diminishing returns after 2
  const powerCount = cleanWords.filter(w => POWER_WORDS.has(w)).length;
  const powerScore = 1 - Math.exp(-powerCount * 0.7);

  // Emotional words — diminishing returns after 2
  const emotionalCount = cleanWords.filter(w => EMOTIONAL_WORDS.has(w)).length;
  const emotionalScore = 1 - Math.exp(-emotionalCount * 0.8);

  // Curiosity gap — weighted pattern matching (max 2 best-matching patterns count)
  let curiosityHits = [];
  for (const { pattern, weight } of CURIOSITY_PATTERNS) {
    if (pattern.test(title)) curiosityHits.push(weight);
  }
  curiosityHits.sort((a, b) => b - a);
  const curiosityRaw = curiosityHits.slice(0, 2).reduce((s, w) => s + w, 0);
  const curiosityScore = Math.min(curiosityRaw / 2.5, 1);

  // Numbers — list posts and specific stats outperform
  const numbers = title.match(/\d+/g) || [];
  // List numbers only count if preceded by a list-like word or used in a title-appropriate context
  // Filter out numbers embedded in alphanumeric tokens (Q3, 5G, G7, etc.)
  const standaloneNumbers = numbers.filter(n => {
    const idx = title.indexOf(n);
    const before = idx > 0 ? title[idx - 1] : ' ';
    const after = idx + n.length < title.length ? title[idx + n.length] : ' ';
    return !/[a-zA-Z]/.test(before) && !/[a-zA-Z]/.test(after);
  });
  const hasListNumber = standaloneNumbers.some(n => { const v = parseInt(n); return v >= 3 && v <= 100; });
  const hasListContext = /^(top|best|worst|\d+\s+(things|ways|habits|skills|tricks|hacks|tips|steps|rules|secrets|reasons|facts|gadgets|products|items|books|mistakes|purchases|apps|technologies|lies|supplements|exercises))/i.test(title);
  const hasYear = numbers.some(n => { const v = parseInt(n); return v >= 2020 && v <= 2030; });
  const hasSpecificStat = numbers.some(n => { const v = parseInt(n); return v > 100; });
  const hasBigMoney = /\$[\d,]{4,}/.test(title) || /\d{1,3}(,\d{3})+/.test(title); // $1,000+ or comma-separated big numbers
  let numberScore = 0;
  if (hasListNumber && hasListContext) numberScore = 0.7;
  else if (hasSpecificStat) numberScore = 0.6;
  else if (hasListNumber) numberScore = 0.45; // 2+ digit number, no strong list context
  else if (standaloneNumbers.length > 0) numberScore = 0.15; // bare standalone numbers — minimal signal
  else if (numbers.length > 0) numberScore = 0.05; // embedded numbers like "Q3", "5G" — near-zero signal

  // Length — Gaussian centered at 50 chars
  const len = title.length;
  const lengthScore = Math.exp(-Math.pow((len - 50) / 25, 2));

  // ALL CAPS emphasis
  const capsWords = words.filter(w => w.length > 1 && w === w.toUpperCase() && /[A-Z]/.test(w)).length;
  const capsScore = capsWords === 0 ? 0 : capsWords <= 2 ? 0.5 + capsWords * 0.15 : 0.7;

  // Brackets/parentheses
  const bracketMatch = title.match(/[\[\(][^\]\)]+[\]\)]/g) || [];
  const bracketScore = bracketMatch.length > 0 ? 0.4 : 0;

  // Urgency
  const urgencyCount = cleanWords.filter(w => URGENCY_WORDS.has(w)).length;
  const urgencyScore = 1 - Math.exp(-urgencyCount * 0.9);

  // Personal pronouns
  const pronounCount = cleanWords.filter(w => PERSONAL_PRONOUNS.has(w)).length;
  const personalScore = Math.min(pronounCount / 3, 1);

  // Emoji
  const emojiRegex = /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/gu;
  const emojiCount = (title.match(emojiRegex) || []).length;
  const emojiScore = emojiCount > 0 ? Math.min(emojiCount * 0.25, 0.5) : 0;

  // Negative framing — loss aversion
  const negativeWords = new Set(['dont', 'stop', 'never', 'worst', 'wrong', 'fail', 'avoid', 'mistake', 'bad', 'terrible', 'horrible', 'ruin']);
  const negativeCount = cleanWords.filter(w => negativeWords.has(w)).length;
  const negativeFraming = Math.min(negativeCount / 2, 1);

  // Contrast/comparison — "$1 vs $1,000,000" is a proven viral format
  const hasVs = /\bvs\.?\b|versus/i.test(title);
  const hasDollarContrast = /\$[\d,]+.*\bvs\.?\b.*\$[\d,]+|\$[\d,]+.*\bvs\.?\b/i.test(title);
  let contrastScore = 0;
  if (hasDollarContrast) contrastScore = 0.85;      // "$1 vs $1,000,000" — extremely proven format
  else if (hasVs) contrastScore = 0.55;              // "X vs Y" — strong format
  else if (/better than|compared|difference between/i.test(title)) contrastScore = 0.35;

  // Narrative/story structure
  const NARRATIVE_PATTERNS = [
    { pattern: /I (tried|tested|spent|survived|lived|built|gave|found|asked|adopted|ate|flew|walked|beat|played|trained|followed|wore|moved|used)/i, weight: 0.4 },
    { pattern: /— .{10,}/i, weight: 0.35 },
    { pattern: /(gone wrong|what happened|the results|here's what|this is what)/i, weight: 0.3 },
    { pattern: /so you don't have to/i, weight: 0.35 },
    { pattern: /here'?s (what|how|why)/i, weight: 0.3 },
    { pattern: /they (said|told|don't)/i, weight: 0.3 },
    { pattern: /I proved/i, weight: 0.35 },
    { pattern: /\(.*\)/, weight: 0.2 },
    { pattern: /for \d+ (days|hours|months|weeks|years)/i, weight: 0.25 },
    { pattern: /gave .* \$[\d,]+/i, weight: 0.3 },
    { pattern: /made me (cry|laugh|quit|think)/i, weight: 0.35 },
    { pattern: /changed (my|everything|the world)/i, weight: 0.25 },
    { pattern: /— (then|and then|but then)/i, weight: 0.3 },
    { pattern: /(he|she|they|a stranger) (quit|walked|paid|gave|sold|left|dropped)/i, weight: 0.35 },
    { pattern: /paid off/i, weight: 0.3 },
    { pattern: /(to save|to find|to help|to prove)/i, weight: 0.25 },
    { pattern: /it actually worked/i, weight: 0.3 },
    { pattern: /made me (successful|rich|better|stronger|healthier|smarter|confident|happy|fit|productive)/i, weight: 0.35 },
    { pattern: /giving .{2,} (people|strangers|kids|families)/i, weight: 0.3 },
    { pattern: /I (went back|returned) to/i, weight: 0.3 },
  ];
  let narrativeRaw = 0;
  for (const { pattern, weight } of NARRATIVE_PATTERNS) {
    if (pattern.test(title)) narrativeRaw += weight;
  }
  const narrativeScore = Math.min(narrativeRaw / 1.5, 1);

  // Readability
  function countSyllables(word) {
    word = word.toLowerCase().replace(/[^a-z]/g, '');
    if (word.length <= 2) return 1;
    word = word.replace(/e$/, '');
    const vowelGroups = word.match(/[aeiouy]+/g);
    return vowelGroups ? Math.max(1, vowelGroups.length) : 1;
  }
  const avgSyllables = cleanWords.length > 0
    ? cleanWords.reduce((sum, w) => sum + countSyllables(w), 0) / cleanWords.length
    : 2;
  const readabilityScore = Math.exp(-Math.pow((avgSyllables - 1.4) / 0.8, 2));

  // Specificity — concrete details beat vague generalizations
  let specificityRaw = 0;
  if (/\$[\d,]+/.test(title)) specificityRaw += 0.4;
  if (/\d+\s*(hours?|days?|weeks?|months?|years?|minutes?)/i.test(title)) specificityRaw += 0.35;
  if (hasSpecificStat) specificityRaw += 0.3;
  if (/\d+%/.test(title)) specificityRaw += 0.25;
  if (hasBigMoney) specificityRaw += 0.3;
  // Named entities — detect real proper nouns (not just Title Case)
  const namedEntities = /\b(Google|Apple|Tesla|Amazon|Netflix|Microsoft|NASA|NFL|NBA|iPhone|Android|PlayStation|Xbox|Minecraft|Fortnite|TikTok|YouTube|Instagram|Twitter|GTA|Ramsay|Elon|Musk|Navy SEAL)\b/i.test(title);
  if (namedEntities) specificityRaw += 0.25;
  const specificityScore = Math.min(specificityRaw / 1.0, 1);

  // YouTube format detection — proven formats viewers recognize
  const FORMAT_TEMPLATES = [
    { pattern: /^I (tried|tested|spent|survived|lived) .+ for \d+/i, weight: 0.7 },   // challenge
    { pattern: /\$[\d,]+.*\bvs\.?\b.*\$[\d,]+/i, weight: 0.7 },                       // $X vs $Y
    { pattern: /world'?s (largest|biggest|smallest|longest|fastest|most|first)/i, weight: 0.65 }, // world record
    { pattern: /^(top|best|worst) \d+/i, weight: 0.55 },                               // ranking
    { pattern: /react(s|ing)? to/i, weight: 0.45 },                                    // reaction
    { pattern: /^how to .+ in \d+/i, weight: 0.5 },                                    // tutorial
    { pattern: /before.*(and|vs|&).*after/i, weight: 0.55 },                           // transformation
    { pattern: /I (made|earned|lost|spent) \$[\d,]+/i, weight: 0.6 },                  // money format
    { pattern: /day in (my|the) life/i, weight: 0.4 },                                 // day-in-life
    { pattern: /\d+ things (I|you|we)/i, weight: 0.5 },                                // list format
    { pattern: /honest review/i, weight: 0.45 },                                       // review
    { pattern: /what I learned/i, weight: 0.45 },                                      // lessons
    { pattern: /I (built|made|created) .{5,}/i, weight: 0.5 },                         // build/create
    { pattern: /(last to|first to) .+ wins/i, weight: 0.6 },                           // competition
    { pattern: /I (gave|surprised|bought) .{5,}/i, weight: 0.5 },                      // generosity
    { pattern: /\d+ (ways|habits|skills|tricks|hacks|things|steps|rules|tips)/i, weight: 0.5 }, // list
    { pattern: /without .{3,}/i, weight: 0.4 },                                        // constraint challenge
    { pattern: /for the first time/i, weight: 0.45 },                                  // first time
    { pattern: /in \d+ (hours|days|minutes)/i, weight: 0.45 },                         // time constraint
    { pattern: /every .+ in/i, weight: 0.45 },                                         // "every X in Y"
    { pattern: /(eating|living|surviving|using) only .+ for/i, weight: 0.55 },           // constraint challenge
    { pattern: /(former|ex-?) .+ (reveals?|explains?|shows?|teaches?)/i, weight: 0.5 },  // authority reveal
  ];
  let formatRaw = 0;
  for (const { pattern, weight } of FORMAT_TEMPLATES) {
    if (pattern.test(title)) formatRaw = Math.max(formatRaw, weight);
  }
  const formatScore = formatRaw;

  // Superlative/scale signal — "World's Largest", "The Most Expensive", extremes grab attention
  let superlativeScore = 0;
  if (/world'?s (largest|biggest|smallest|longest|fastest|most|first|deadliest|hardest|worst|best|tallest|deepest)/i.test(title)) superlativeScore = 0.8;
  else if (/(the )?(most|least|biggest|smallest|best|worst|largest|fastest|strongest|craziest|scariest|hardest|greatest|weirdest|strangest) .{3,}/i.test(title)) superlativeScore = 0.6;
  else if (/every(thing|one)?|entire|all \d+|100%/i.test(title)) superlativeScore = 0.4;
  else if (/(never|no one|nobody|nothing|impossible|only \d)/i.test(title)) superlativeScore = 0.35;
  // Significance markers
  if (/(in (human )?history|of all time|ever (made|seen|built|created|recorded)|in the world)/i.test(title)) superlativeScore = Math.max(superlativeScore, 0.5);

  // Scale/magnitude — big numbers and extreme amounts create intrigue
  let scaleScore = 0;
  const dollarMatches = title.match(/\$[\d,]+/g) || [];
  const maxDollarVal = Math.max(0, ...dollarMatches.map(d => parseInt(d.replace(/[$,]/g, '')) || 0));
  if (maxDollarVal >= 1000000) scaleScore = 0.9;      // millions
  else if (maxDollarVal >= 100000) scaleScore = 0.75;  // hundred thousands
  else if (maxDollarVal >= 10000) scaleScore = 0.6;    // ten thousands
  else if (maxDollarVal >= 1000) scaleScore = 0.45;    // thousands
  // Non-dollar large numbers also create scale
  const allNums = numbers.map(n => parseInt(n) || 0);
  const maxNum = Math.max(0, ...allNums);
  if (scaleScore === 0) {
    if (maxNum >= 1000000) scaleScore = 0.7;
    else if (maxNum >= 100000) scaleScore = 0.55;
    else if (maxNum >= 10000) scaleScore = 0.4;
  }

  // Intrigue/challenge signal — implies something surprising, impossible, or extreme
  let intrigueScore = 0;
  if (/without (any|a single|breaking|taking|leaving|using|spending|touching)/i.test(title)) intrigueScore = Math.max(intrigueScore, 0.6);
  if (/(only|just) \$?\d/i.test(title)) intrigueScore = Math.max(intrigueScore, 0.5);
  if (/no (money|food|water|sleep|phone|technology|rules)/i.test(title)) intrigueScore = Math.max(intrigueScore, 0.55);
  if (/(won't believe|will shock|mind-blowing|blew my mind|jaw dropped)/i.test(title)) intrigueScore = Math.max(intrigueScore, 0.4);
  if (/banned|illegal|forbidden|not allowed|shouldn't|can't be|cannot be/i.test(title)) intrigueScore = Math.max(intrigueScore, 0.55);
  if (/(the real|the truth|the dark side|the ugly truth|the secret|the hidden)/i.test(title)) intrigueScore = Math.max(intrigueScore, 0.5);
  if (/(no one|nobody) (knows|talks|believes|expected|told)/i.test(title)) intrigueScore = Math.max(intrigueScore, 0.55);
  if (/\?$/.test(title) && wordCount >= 5) intrigueScore = Math.max(intrigueScore, 0.35);
  // "The X that/who [dramatic verb]" — classic viral structure
  if (/the \w+ (that|who|which) (broke|changed|saved|ended|destroyed|ruined|transformed|created|built|beat|solved|proved|shocked|invented|killed|made|catches|caught|trapped|stopped|predicted|can't|cannot|couldn't|won't|shouldn't|wouldn't)/i.test(title)) intrigueScore = Math.max(intrigueScore, 0.6);
  // Dramatic juxtaposition — mundane thing + extreme context
  if (/(from|in|into) (space|the moon|the ocean|the desert|the wild|the void|orbit|jupiter|a black hole)/i.test(title)) intrigueScore = Math.max(intrigueScore, 0.55);
  if (/(fell into|dropped from|launched into|poured .* on|survived) /i.test(title)) intrigueScore = Math.max(intrigueScore, 0.5);
  // Paradigm-breaking claims
  if (/(not a force|doesn't exist|is (a )?lie|is wrong|is fake|is a scam|fundamental flaw|broke physics|end humanity|impossible)/i.test(title)) intrigueScore = Math.max(intrigueScore, 0.6);
  // Emotional outcome implied
  if (/(made everyone|will make you|made me) (cry|laugh|quit|think|scream|gasp)/i.test(title)) intrigueScore = Math.max(intrigueScore, 0.5);
  // Dramatic verbs in title (even without pattern)
  const DRAMATIC_WORDS = new Set(['broke', 'destroyed', 'shattered', 'exposed', 'ended', 'died', 'survived', 'escaped', 'trapped', 'stranded', 'abandoned', 'haunted', 'crashed', 'exploded', 'dominated', 'conquered']);
  const dramaticCount = cleanWords.filter(w => DRAMATIC_WORDS.has(w)).length;
  if (dramaticCount > 0) intrigueScore = Math.max(intrigueScore, 0.3 + dramaticCount * 0.15);
  // Intellectual curiosity — unusual/intriguing adjectives that make you want to learn more
  const INTRIGUE_WORDS = new Set(['bizarre', 'strange', 'weird', 'mysterious', 'paradox', 'counterintuitive', 'unexpected', 'surprising', 'unreasonable', 'impossible', 'unsolvable', 'unexplained', 'forbidden', 'forgotten', 'lost', 'ancient', 'unknown', 'rare']);
  const intrigueWordCount = cleanWords.filter(w => INTRIGUE_WORDS.has(w)).length;
  if (intrigueWordCount > 0) intrigueScore = Math.max(intrigueScore, 0.35 + intrigueWordCount * 0.15);
  // Educational "How X works/happens" with substance (5+ words)
  if (/^how .{20,}/i.test(title) && wordCount >= 5) intrigueScore = Math.max(intrigueScore, 0.3);
  // Mystery structures: "The X that/where/which [optional words] [intriguing verb]"
  if (/the \w+ (that|where|which|who) .{0,25}(predicts?|moves?|lives?|turns?|changes?|disappears?|grows?|sleeps?|hides?|watches?|kills?|eats?|floats?|burns?|glows?|speaks?|remembers?|forgets?|waits?|listens?)/i.test(title)) intrigueScore = Math.max(intrigueScore, 0.5);
  // "X At The Bottom/Top/Edge Of" — location-based mystery
  if (/(at the (bottom|top|edge|end|center|heart|depths?) of|under the (ice|ocean|sea|ground|surface|earth))/i.test(title)) intrigueScore = Math.max(intrigueScore, 0.45);
  // "Eating/Living/Surviving Only X" constraint patterns
  if (/(eating|living|surviving|using|drinking|wearing) only/i.test(title)) intrigueScore = Math.max(intrigueScore, 0.4);
  // Expert/authority + reveal/explain structure
  if (/(agent|spy|scientist|doctor|engineer|professor|astronaut|chef|surgeon|detective|soldier|officer|guard|hacker|pilot|diver|biologist|archaeologist) .*(reveals?|explains?|shows?|teaches?|shares?|discovers?)/i.test(title)) intrigueScore = Math.max(intrigueScore, 0.5);
  // Prohibition/forbidden — "nobody is allowed", "can't be opened/visited"
  if (/(nobody|no one|not allowed|isn't allowed|can't be|cannot be|shouldn't be|forbidden|prohibited) .*(visit|open|enter|see|touch|go|access|know|tell)/i.test(title)) intrigueScore = Math.max(intrigueScore, 0.6);
  if (/(nobody|no one) is allowed/i.test(title)) intrigueScore = Math.max(intrigueScore, 0.55);
  // Self-taught / impossible achievement
  if (/taught (herself|himself|themselves|myself)/i.test(title)) intrigueScore = Math.max(intrigueScore, 0.55);
  // Trade-up / progression narrative ("traded X for Y", "turned X into Y")
  if (/(traded|turned|transformed|converted) .{2,} (for|into) .{2,}/i.test(title)) intrigueScore = Math.max(intrigueScore, 0.55);
  // Paradox: "simplest/easiest X no one can Y", "shouldn't exist/be alive"
  if (/(simplest|easiest|most basic|most obvious) .{3,} (no one|nobody) can/i.test(title)) intrigueScore = Math.max(intrigueScore, 0.6);
  if (/shouldn't (exist|be alive|be possible|work|be real)/i.test(title)) intrigueScore = Math.max(intrigueScore, 0.55);
  // "Something [verb] at/in/under" — vague mystery
  if (/something .{2,} (at|in|under|inside|behind|beneath|below)/i.test(title)) intrigueScore = Math.max(intrigueScore, 0.5);
  // Repeated extreme action: "[verb] X [N] times"
  if (/\d+\s*times/i.test(title) && /(ran|went|tried|did|ate|jumped|climbed|entered|returned|called)/i.test(title)) intrigueScore = Math.max(intrigueScore, 0.45);
  // "The X that/which broke/changed/solved" — already partially covered but ensure strong match
  if (/(breaks?|broke|changed?s?|solved?s?|ended?s?|shattered?s?|defie[sd]|bent|rewrote|rewrites?) .{0,10}(physics|science|math|the internet|the rules|every rule|every law|everything|the world|reality)/i.test(title)) intrigueScore = Math.max(intrigueScore, 0.6);
  // "They Found X" — discovery narrative
  if (/they found/i.test(title)) intrigueScore = Math.max(intrigueScore, 0.5);
  // "How X Makes/Does Y" — counterintuitive mechanism
  if (/^how .{3,} (makes?|does|causes?|creates?|prevents?|kills?|saves?) /i.test(title) && wordCount >= 6) intrigueScore = Math.max(intrigueScore, 0.4);
  // "Anything You Can X" — open challenge format
  if (/anything you can/i.test(title)) intrigueScore = Math.max(intrigueScore, 0.5);
  // Absurd contrast — juxtaposing powerful/expensive with weak/cheap
  if (/(formula 1|ferrari|lamborghini|private jet|yacht|mansion|tank|helicopter) .{2,} (bicycle|skateboard|scooter|wheelchair|shopping cart|feet|walking|tricycle|$1|penny)/i.test(title) ||
      /(bicycle|skateboard|scooter|$1|penny) .{2,} (formula 1|ferrari|lamborghini|private jet|yacht|mansion|tank|helicopter)/i.test(title)) intrigueScore = Math.max(intrigueScore, 0.6);
  // "Went back to X grade/school" — regression/nostalgia challenge
  if (/(went back|returned) to .{1,10}(grade|school|kindergarten|preschool)/i.test(title)) intrigueScore = Math.max(intrigueScore, 0.5);

  // Action/conflict signal — verbs implying conflict, justice, or consequence
  let actionScore = 0;
  if (/(catches|caught|traps|trapped|stops|stopped|busted|fights|chases|steals|stealing|hunted|hunting)/i.test(title)) actionScore = Math.max(actionScore, 0.55);
  // Action implies intrigue when there's a target (thief, criminal, etc.)
  if (/(catches|caught|traps|trapped|busted|hunted|hunting|chases) .*(thief|thieves|criminal|scammer|cheater|liar|pirate|burglar|robber|intruder)/i.test(title)) intrigueScore = Math.max(intrigueScore, 0.55);
  if (/(reacts? to|responds? to|reviews?|tries|tested|rated|ranked|reveals?|explains?|teaches|shows|shares|discovers?)/i.test(title)) actionScore = Math.max(actionScore, 0.4);
  if (/(wins?|loses?|beats?|defeats?|dominates?|crushes?)/i.test(title)) actionScore = Math.max(actionScore, 0.45);
  if (/breaks down/i.test(title)) actionScore = Math.max(actionScore, 0.5); // expert analysis format
  if (/(taste|blind|lab|drop|crash|stress|speed|durability) test/i.test(title)) actionScore = Math.max(actionScore, 0.5); // testing format

  // Transformation/underdog signal — rags to riches, before/after, origin stories
  let transformScore = 0;
  if (/(became a|went from|turned into|grew up to|rose to)/i.test(title)) transformScore = 0.6;
  else if (/(were invented|was (discovered|created|born|built|made|found))/i.test(title)) transformScore = 0.45;
  else if (/(changed (my|his|her|their|the|everything)|saved (my|his|her|the))/i.test(title)) transformScore = 0.4;
  else if (/(dream (house|car|job|home)|first time)/i.test(title)) transformScore = 0.35;
  // "The [humble person] Who [achievement]" — underdog stories
  if (/the (girl|boy|kid|man|woman|teen|student|janitor|dropout|homeless|blind|deaf|disabled|8-year-old|\d+-year-old) who/i.test(title)) transformScore = Math.max(transformScore, 0.6);
  // Age-based surprise (very young or old doing something unexpected)
  if (/(\d+).?year.?old/i.test(title)) {
    const ageMatch = title.match(/(\d+).?year.?old/i);
    const age = parseInt(ageMatch[1]);
    if (age <= 12 || age >= 70) transformScore = Math.max(transformScore, 0.5);
  }
  // Disability + achievement
  if (/(blind|deaf|paralyzed|wheelchair|amputee|disabled) .{3,}(won|beat|made|built|created|became|teaches|chef|doctor|athlete|champion)/i.test(title)) transformScore = Math.max(transformScore, 0.65);
  // "Was [bad state] — Now [good state]"
  if (/was .{3,}(now|today)/i.test(title) && /(homeless|broke|poor|failing|dropout|lost|fired|bankrupt)/i.test(title)) transformScore = Math.max(transformScore, 0.6);
  // Reunited / emotional outcome
  if (/(reunited|reunion|found .{2,} after \d+|met .{2,} for the first time)/i.test(title)) transformScore = Math.max(transformScore, 0.5);

  // Authority/expert signal — credible source adds weight
  let authorityScore = 0;
  if (/(chef|doctor|scientist|engineer|expert|professional|professor|master|surgeon|astronaut|olympian|champion|billionaire|investor|CEO|founder|millionaire)/i.test(title)) authorityScore = 0.45;
  if (/(michelin|harvard|mit|nasa|navy seal|special forces|world record|guinness)/i.test(title)) authorityScore = Math.max(authorityScore, 0.55);
  // Authority + contrast = high engagement
  if (authorityScore > 0 && /(cheap|free|\$\d|fast food|school|kids|beginner|worst|basic)/i.test(title)) authorityScore = Math.min(authorityScore + 0.2, 0.8);

  // Exclamation marks
  const exclamCount = (title.match(/!/g) || []).length;
  const exclamScore = exclamCount === 0 ? 0 : exclamCount <= 2 ? 0.3 : 0.15;

  // Word count score (used in CTR sim, not in quality composite)
  let wordCountScore;
  if (wordCount >= 6 && wordCount <= 12) wordCountScore = 1;
  else if (wordCount >= 4 && wordCount < 6) wordCountScore = 0.8;
  else if (wordCount > 12 && wordCount <= 16) wordCountScore = 0.7;
  else if (wordCount < 4) wordCountScore = 0.3;
  else wordCountScore = 0.4;

  // --- Compound signals ---
  const hookStrength = curiosityScore * 0.6 + powerScore * 0.4;
  const relatability = emotionalScore * 0.5 + personalScore * 0.5;
  const concreteness = numberScore * 0.4 + specificityScore * 0.3 + scaleScore * 0.3;
  const storyStrength = narrativeScore * 0.6 + relatability * 0.4;

  // --- Composite title quality (peak-based approach) ---
  // Only APPEAL signals — not hygiene factors like readability, length, word count
  const allSignals = [
    curiosityScore, powerScore, emotionalScore, numberScore,
    capsScore, urgencyScore, personalScore, negativeFraming,
    narrativeScore, specificityScore, formatScore, contrastScore,
    superlativeScore, scaleScore, intrigueScore, bracketScore,
    actionScore, transformScore, authorityScore,
  ];

  // Peak score: weighted top-3 — best signal matters most, single strong signal can carry
  const sortedSignals = [...allSignals].sort((a, b) => b - a);
  const peakScore = (sortedSignals[0] || 0) * 0.60 + (sortedSignals[1] || 0) * 0.25 + (sortedSignals[2] || 0) * 0.15;

  // Breadth: how many signals are active (above 0.15)
  const activeCount = allSignals.filter(s => s > 0.15).length;
  const breadthScore = Math.min(activeCount / 6, 1); // 6+ active signals = full breadth

  // Compound bonuses — multi-dimensional titles get extra credit
  const hookBonus = hookStrength > 0.25 ? hookStrength * 0.15 : 0;
  const storyBonus = storyStrength > 0.2 ? storyStrength * 0.15 : 0;
  const concreteBonus = concreteness > 0.25 ? concreteness * 0.12 : 0;

  // Readability modifier — very complex language drags score down
  const readabilityMod = 0.82 + readabilityScore * 0.18; // 0.82x to 1.0x

  // Length modifier — only penalize extremes
  let lengthMod = 1.0;
  if (len < 10) lengthMod = 0.5 + (len / 10) * 0.5;       // under 10 chars: harsh
  else if (len > 100) lengthMod = Math.max(0.4, 1 - (len - 100) / 120); // very long: gradual drop

  // Combine: peak quality + breadth + compound bonuses
  let rawQuality = peakScore * 0.70 + breadthScore * 0.10 + hookBonus + storyBonus + concreteBonus;

  // Apply modifiers
  rawQuality = rawQuality * readabilityMod * lengthMod;

  // Clickbait substance penalty — all hook, no substance = empty clickbait
  // Intrigue partially counts as substance (it implies a specific mystery/promise)
  const hookSignals = powerScore + urgencyScore + capsScore + (curiosityScore > 0.5 ? 0.3 : 0);
  const substanceSignals = specificityScore + narrativeScore + numberScore + formatScore + contrastScore + authorityScore + transformScore + actionScore + intrigueScore * 0.7;
  if (hookSignals > 1.0 && substanceSignals < 0.25) {
    rawQuality *= 0.3; // heavy penalty for empty clickbait
  } else if (hookSignals > 0.7 && substanceSignals < 0.15) {
    rawQuality *= 0.5;
  }

  // Word count: only penalize truly bare titles (1-2 words)
  if (wordCount <= 1) rawQuality *= 0.12;
  else if (wordCount === 2) rawQuality *= 0.4;

  // Apply power curve to spread the range
  const titleQuality = Math.min(Math.pow(Math.max(rawQuality, 0), 0.8), 1.0);

  const signals = {
    powerScore,
    emotionalScore,
    curiosityScore,
    numberScore,
    lengthScore,
    capsScore,
    urgencyScore,
    personalScore,
    negativeFraming,
    contrastScore,
    narrativeScore,
    readabilityScore,
    specificityScore,
    formatScore,
    superlativeScore,
    scaleScore,
    intrigueScore,
    wordCountScore,
    emojiScore,
    actionScore,
    transformScore,
    authorityScore,
    // Compound
    hookStrength,
    relatability,
    concreteness,
    storyStrength,
    // Final
    titleQuality,
  };

  return signals;
}

// ============================================================
//  VIEWER ARCHETYPES
// ============================================================

const ARCHETYPES = [
  { name: 'Casual Scroller', weight: 0.22, baseCTR: 0.025,
    traits: { attentionSpan: 0.3, emotionalSensitivity: 0.5, curiosityDrive: 0.4, numberBias: 0.3, skepticism: 0.2, platformFatigue: 0.6, urgencyResponse: 0.3, personalConnection: 0.4, emojiAffinity: 0.5 } },
  { name: 'Distracted Scroller', weight: 0.15, baseCTR: 0.008,
    traits: { attentionSpan: 0.15, emotionalSensitivity: 0.2, curiosityDrive: 0.1, numberBias: 0.1, skepticism: 0.3, platformFatigue: 0.9, urgencyResponse: 0.1, personalConnection: 0.15, emojiAffinity: 0.2 } },
  { name: 'Passive Viewer', weight: 0.18, baseCTR: 0.015,
    traits: { attentionSpan: 0.2, emotionalSensitivity: 0.4, curiosityDrive: 0.2, numberBias: 0.2, skepticism: 0.4, platformFatigue: 0.8, urgencyResponse: 0.2, personalConnection: 0.3, emojiAffinity: 0.3 } },
  { name: 'Impulse Clicker', weight: 0.12, baseCTR: 0.055,
    traits: { attentionSpan: 0.4, emotionalSensitivity: 0.8, curiosityDrive: 0.7, numberBias: 0.6, skepticism: 0.1, platformFatigue: 0.4, urgencyResponse: 0.9, personalConnection: 0.6, emojiAffinity: 0.7 } },
  { name: 'Trend Follower', weight: 0.08, baseCTR: 0.045,
    traits: { attentionSpan: 0.5, emotionalSensitivity: 0.6, curiosityDrive: 0.6, numberBias: 0.7, skepticism: 0.3, platformFatigue: 0.3, urgencyResponse: 0.7, personalConnection: 0.5, emojiAffinity: 0.6 } },
  { name: 'Knowledge Seeker', weight: 0.08, baseCTR: 0.05,
    traits: { attentionSpan: 0.85, emotionalSensitivity: 0.15, curiosityDrive: 0.95, numberBias: 0.6, skepticism: 0.6, platformFatigue: 0.15, urgencyResponse: 0.1, personalConnection: 0.2, emojiAffinity: 0.05 } },
  { name: 'Skeptic', weight: 0.07, baseCTR: 0.02,
    traits: { attentionSpan: 0.6, emotionalSensitivity: 0.2, curiosityDrive: 0.5, numberBias: 0.4, skepticism: 0.9, platformFatigue: 0.5, urgencyResponse: 0.1, personalConnection: 0.3, emojiAffinity: 0.1 } },
  { name: 'Deep Diver', weight: 0.05, baseCTR: 0.06,
    traits: { attentionSpan: 0.9, emotionalSensitivity: 0.3, curiosityDrive: 0.8, numberBias: 0.5, skepticism: 0.7, platformFatigue: 0.2, urgencyResponse: 0.2, personalConnection: 0.5, emojiAffinity: 0.1 } },
  { name: 'Binge Watcher', weight: 0.05, baseCTR: 0.065,
    traits: { attentionSpan: 0.8, emotionalSensitivity: 0.7, curiosityDrive: 0.9, numberBias: 0.4, skepticism: 0.3, platformFatigue: 0.1, urgencyResponse: 0.4, personalConnection: 0.7, emojiAffinity: 0.4 } },
];

// ============================================================
//  PROFILE GENERATION
// ============================================================

function generateProfile(rng) {
  const roll = rng();
  let cumulative = 0;
  let archetype = ARCHETYPES[0];
  for (const a of ARCHETYPES) {
    cumulative += a.weight;
    if (roll <= cumulative) { archetype = a; break; }
  }

  // Trait noise: ±0.2 on archetype base
  const traits = {};
  for (const [key, base] of Object.entries(archetype.traits)) {
    traits[key] = Math.max(0, Math.min(1, base + (rng() - 0.5) * 0.4));
  }

  // --- Context (random per impression, not per archetype) ---
  traits.topicRelevance = Math.pow(rng(), 0.5); // more viewers see irrelevant content on Explore
  traits.mood = rng();
  traits.sessionDepth = Math.pow(rng(), 0.8);
  traits.scrollPosition = rng();
  traits.timeOfDay = rng();
  traits.competitionLevel = 0.4 + rng() * 0.6; // always some competition on Explore

  const baseCTR = archetype.baseCTR;

  return { archetype: archetype.name, baseCTR, traits };
}

// ============================================================
//  CLICK DECISION
// ============================================================

function wouldClick(profile, signals, rng, options = {}) {
  const t = profile.traits;
  const tq = signals.titleQuality; // 0 to ~0.85 for great titles, 0.1 for bad

  // ========================================
  // STEP 1: Title quality multiplier (THE KEY CHANGE)
  // A title with quality 0.8 produces ~3-4x the CTR of quality 0.2
  // Uses exponential so small differences at the top still matter
  // ========================================
  // Hockey-stick curve: steeper separation between bad and great
  // tq=0.1→0.39x, tq=0.3→0.99x, tq=0.5→2.66x, tq=0.7→6.12x, tq=0.9→12.73x
  const qualityMultiplier = 0.15 + Math.pow(tq, 1.0) * 2.5 + Math.pow(tq, 2.5) * 12.0;

  // ========================================
  // STEP 2: Signal-trait resonance
  // How well does THIS title match THIS viewer's personality?
  // ========================================
  let resonance = 0;

  // Curiosity gap × curiosity drive (strongest interaction)
  resonance += signals.curiosityScore * t.curiosityDrive * 0.35;

  // Emotional hook × emotional sensitivity
  resonance += signals.emotionalScore * t.emotionalSensitivity * 0.25;

  // Power words × attention span (grabs attention of low-attention viewers differently)
  resonance += signals.powerScore * (1.2 - t.attentionSpan) * 0.15; // power words help MORE with low attention

  // Numbers × number bias
  resonance += signals.numberScore * t.numberBias * 0.15;

  // Urgency × urgency response
  resonance += signals.urgencyScore * t.urgencyResponse * 0.20;

  // Personal pronouns × personal connection
  resonance += signals.personalScore * t.personalConnection * 0.12;

  // Negative framing × curiosity (loss aversion)
  resonance += signals.negativeFraming * t.curiosityDrive * 0.10;

  // Caps emphasis × attention span (caps grab low-attention viewers)
  resonance += signals.capsScore * (1.1 - t.attentionSpan) * 0.08;

  // Contrast × curiosity
  resonance += signals.contrastScore * t.curiosityDrive * 0.08;

  // Narrative × attention span (story hooks engage attentive viewers)
  resonance += signals.narrativeScore * t.attentionSpan * 0.20;

  // Narrative × emotional sensitivity (stories pull emotional viewers)
  resonance += signals.narrativeScore * t.emotionalSensitivity * 0.12;

  // Specificity × skepticism (specific claims convince skeptics)
  resonance += signals.specificityScore * t.skepticism * 0.10;

  // Format × platform fatigue (familiar formats engage even fatigued viewers)
  resonance += signals.formatScore * (1 - t.platformFatigue) * 0.08;

  // AI archetype appeal modifier — makes different viewer types respond differently per title
  let archetypeAppealMult = 1.0;
  if (options.archetypeAppeal) {
    const ARCHETYPE_APPEAL_MAP = {
      'Casual Scroller': 'casualScroller',
      'Distracted Scroller': 'distractedScroller',
      'Passive Viewer': 'passiveViewer',
      'Impulse Clicker': 'impulseClicker',
      'Trend Follower': 'trendFollower',
      'Knowledge Seeker': 'knowledgeSeeker',
      'Skeptic': 'skeptic',
      'Deep Diver': 'deepDiver',
      'Binge Watcher': 'bingeWatcher',
    };
    const key = ARCHETYPE_APPEAL_MAP[profile.archetype];
    if (key && typeof options.archetypeAppeal[key] === 'number') {
      // Appeal 0.0 → 0.4x, appeal 0.5 → 1.0x, appeal 1.0 → 1.8x
      archetypeAppealMult = 0.4 + options.archetypeAppeal[key] * 1.4;
    }
  }

  const resonanceMultiplier = (0.6 + resonance * 1.2) * archetypeAppealMult;

  // ========================================
  // STEP 3: Context layer (lighter touch — ±30% swing, not ±60%)
  // ========================================

  // Topic relevance (still important, but not as crushing)
  const relevanceMult = 0.5 + t.topicRelevance * 0.7; // 0.5x to 1.2x

  // Mood: ±15%
  const moodMult = 0.85 + t.mood * 0.3;

  // Session fatigue: 0% to -20%
  const fatigueMult = 1 - t.sessionDepth * 0.2;

  // Scroll position: 0% to -15%
  const scrollMult = 1 - t.scrollPosition * 0.15;

  // Competition: -15% to -35%
  const competitionMult = 1 - t.competitionLevel * 0.25;

  // Late night boost: up to +15%
  const timeMult = t.timeOfDay > 0.75 ? 1 + (t.timeOfDay - 0.75) * 0.6 : 1;

  // ========================================
  // STEP 4: Penalties
  // ========================================

  // Skepticism penalty — clickbait triggers skeptics
  let skepticismMult = 1;
  const clickbaitLevel = (signals.powerScore * 0.3 + signals.emotionalScore * 0.2 + signals.capsScore * 0.2 + signals.urgencyScore * 0.3);
  if (clickbaitLevel > 0.3) {
    skepticismMult = 1 - (clickbaitLevel - 0.3) * t.skepticism * 0.6;
  }

  // Platform fatigue
  const fatiguePenalty = 1 - t.platformFatigue * 0.15;

  // Emoji backlash
  let emojiFactor = 1;
  if (signals.emojiScore > 0 && t.emojiAffinity < 0.25) {
    emojiFactor = 0.8; // turned off by emojis
  }

  // ========================================
  // STEP 5: Combine everything
  // ========================================
  let clickProb = profile.baseCTR     // archetype base (0.015 to 0.065)
    * qualityMultiplier                // title quality (0.3x to 2.8x) — THE BIG LEVER
    * resonanceMultiplier              // signal-trait match (0.6x to 1.8x)
    * relevanceMult                    // topic relevance (0.5x to 1.2x)
    * moodMult                         // mood (0.85x to 1.15x)
    * fatigueMult                      // session depth (0.8x to 1.0x)
    * scrollMult                       // scroll position (0.85x to 1.0x)
    * competitionMult                  // competition (0.65x to 1.0x)
    * timeMult                         // time of day (1.0x to 1.15x)
    * skepticismMult                   // skepticism penalty
    * fatiguePenalty                    // platform fatigue
    * emojiFactor                      // emoji backlash
    * (options.thumbnailMult || 1);    // thumbnail quality (0.6x to 1.5x)

  // ========================================
  // STEP 6: Chaos events (rare)
  // ========================================

  // 1.5% distraction (notification, phone call, cat on keyboard)
  if (rng() < 0.015) clickProb *= 0.05;

  // 0.5% "this title was MADE for me" moment
  if (rng() < 0.005) clickProb *= 3;

  // 0.3% accidental click
  if (rng() < 0.003) return true;

  // 1% "already seen it" — skip entirely
  if (rng() < 0.01) return false;

  // Soft cap — no real Explore page impression converts above ~15%
  clickProb = Math.max(0.001, Math.min(0.15, clickProb));

  return rng() < clickProb;
}

// ============================================================
//  SIMULATION RUNNER
// ============================================================

export async function simulateTitle(title, profileCount = 100000, onProgress = null, options = {}) {
  const rng = () => Math.random();
  let signals = analyzeTitle(title);

  // Blend AI signal overrides (70% AI, 30% regex) when provided
  if (options.aiSignals) {
    const ai = options.aiSignals;
    const blended = { ...signals };
    const SIGNAL_KEYS = [
      'curiosityScore', 'emotionalScore', 'powerScore', 'urgencyScore',
      'personalScore', 'numberScore', 'capsScore', 'negativeFraming',
      'narrativeScore', 'specificityScore', 'formatScore', 'hookStrength',
      'relatability', 'concreteness', 'storyStrength', 'intrigueScore',
      'actionScore', 'transformScore',
    ];
    for (const key of SIGNAL_KEYS) {
      if (typeof ai[key] === 'number') {
        const regexVal = signals[key] || 0;
        blended[key] = ai[key] * 0.7 + regexVal * 0.3;
      }
    }
    // Recompute titleQuality from blended signals
    const vals = SIGNAL_KEYS.map(k => blended[k] || 0).sort((a, b) => b - a);
    const top3 = vals.slice(0, 3);
    const peakScore = top3[0] * 0.60 + (top3[1] || 0) * 0.25 + (top3[2] || 0) * 0.15;
    const activeCount = vals.filter(v => v > 0.15).length;
    const breadthScore = Math.min(activeCount / 8, 1.0);
    blended.titleQuality = Math.min(peakScore * 0.70 + breadthScore * 0.10, 1.0);
    signals = blended;
  }

  let clicks = 0;
  const archetypeClicks = {};
  const archetypeTotals = {};

  for (const a of ARCHETYPES) {
    archetypeClicks[a.name] = 0;
    archetypeTotals[a.name] = 0;
  }

  // Track per-batch CTR for confidence intervals (10 batches)
  const CI_BATCHES = 10;
  const batchSize = Math.floor(profileCount / CI_BATCHES);
  const batchCTRs = [];
  let batchClicks = 0;
  let batchCount = 0;

  const BATCH = 2000;
  for (let i = 0; i < profileCount; i += BATCH) {
    const end = Math.min(i + BATCH, profileCount);
    for (let j = i; j < end; j++) {
      const profile = generateProfile(rng, options);
      archetypeTotals[profile.archetype]++;
      if (wouldClick(profile, signals, rng, options)) {
        clicks++;
        archetypeClicks[profile.archetype]++;
        batchClicks++;
      }
      batchCount++;
      if (batchCount >= batchSize && batchCTRs.length < CI_BATCHES - 1) {
        batchCTRs.push(batchClicks / batchCount);
        batchClicks = 0;
        batchCount = 0;
      }
    }
    if (onProgress) onProgress(end, profileCount);
    await new Promise(r => setTimeout(r, 0));
  }
  // Push final batch
  if (batchCount > 0) batchCTRs.push(batchClicks / batchCount);

  const ctr = clicks / profileCount;

  // Compute confidence interval from batch CTRs
  const sortedBatches = [...batchCTRs].sort((a, b) => a - b);
  const ctrLow = sortedBatches[Math.floor(sortedBatches.length * 0.1)] || ctr;
  const ctrHigh = sortedBatches[Math.floor(sortedBatches.length * 0.9)] || ctr;

  const archetypeBreakdown = ARCHETYPES.map(a => ({
    name: a.name,
    clicks: archetypeClicks[a.name],
    total: archetypeTotals[a.name],
    ctr: archetypeTotals[a.name] > 0 ? archetypeClicks[a.name] / archetypeTotals[a.name] : 0,
  }));

  return {
    title,
    clicks,
    impressions: profileCount,
    ctr,
    ctrLow,
    ctrHigh,
    signals,
    archetypeBreakdown,
  };
}

export async function simulateTitles(titles, profileCount = 100000, onProgress = null, options = {}) {
  const results = [];
  for (let i = 0; i < titles.length; i++) {
    // Pass per-title AI signal overrides and archetype appeal if available
    let titleOpts = options;
    if (options.aiSignalOverrides?.[i]) {
      const override = options.aiSignalOverrides[i];
      titleOpts = {
        ...options,
        ...(override.scores ? { aiSignals: override.scores } : {}),
        ...(override.archetypeAppeal ? { archetypeAppeal: override.archetypeAppeal } : {}),
      };
    }
    const result = await simulateTitle(
      titles[i],
      profileCount,
      (processed, total) => onProgress && onProgress(i, processed, total),
      titleOpts
    );
    results.push(result);
  }
  return results;
}

// ============================================================
//  HEAD-TO-HEAD COMPETITIVE MODE
//  Same viewer pool sees all titles, picks the best (or none)
// ============================================================

export async function simulateTitlesCompetitive(titles, profileCount = 100000, onProgress = null, options = {}) {
  const rng = () => Math.random();
  const allSignals = titles.map(t => analyzeTitle(t));

  // Per-title tracking
  const clicks = titles.map(() => 0);
  const archetypeClicks = titles.map(() => {
    const obj = {};
    for (const a of ARCHETYPES) obj[a.name] = 0;
    return obj;
  });
  const archetypeTotals = {};
  for (const a of ARCHETYPES) archetypeTotals[a.name] = 0;

  const BATCH = 2000;
  for (let i = 0; i < profileCount; i += BATCH) {
    const end = Math.min(i + BATCH, profileCount);
    for (let j = i; j < end; j++) {
      const profile = generateProfile(rng, options);
      archetypeTotals[profile.archetype]++;

      // Compute click probability for each title
      const probs = allSignals.map(signals => {
        const t = profile.traits;
        const tq = signals.titleQuality;
        const qualityMultiplier = 0.15 + Math.pow(tq, 1.0) * 2.5 + Math.pow(tq, 2.5) * 12.0;

        let resonance = 0;
        resonance += signals.curiosityScore * t.curiosityDrive * 0.35;
        resonance += signals.emotionalScore * t.emotionalSensitivity * 0.25;
        resonance += signals.powerScore * (1.2 - t.attentionSpan) * 0.15;
        resonance += signals.numberScore * t.numberBias * 0.15;
        resonance += signals.urgencyScore * t.urgencyResponse * 0.20;
        resonance += signals.personalScore * t.personalConnection * 0.12;
        resonance += signals.negativeFraming * t.curiosityDrive * 0.10;
        resonance += signals.capsScore * (1.1 - t.attentionSpan) * 0.08;
        resonance += signals.contrastScore * t.curiosityDrive * 0.08;
        resonance += signals.narrativeScore * t.attentionSpan * 0.20;
        resonance += signals.narrativeScore * t.emotionalSensitivity * 0.12;
        resonance += signals.specificityScore * t.skepticism * 0.10;
        resonance += signals.formatScore * (1 - t.platformFatigue) * 0.08;
        const resonanceMultiplier = 0.6 + resonance * 1.2;

        const relevanceMult = 0.5 + t.topicRelevance * 0.7;
        const moodMult = 0.85 + t.mood * 0.3;
        const fatigueMult = 1 - t.sessionDepth * 0.2;
        const scrollMult = 1 - t.scrollPosition * 0.15;
        const competitionMult = 1 - t.competitionLevel * 0.25;
        const timeMult = t.timeOfDay > 0.75 ? 1 + (t.timeOfDay - 0.75) * 0.6 : 1;

        let skepticismMult = 1;
        const clickbaitLevel = (signals.powerScore * 0.3 + signals.emotionalScore * 0.2 + signals.capsScore * 0.2 + signals.urgencyScore * 0.3);
        if (clickbaitLevel > 0.3) skepticismMult = 1 - (clickbaitLevel - 0.3) * t.skepticism * 0.6;
        const fatiguePenalty = 1 - t.platformFatigue * 0.15;
        let emojiFactor = 1;
        if (signals.emojiScore > 0 && t.emojiAffinity < 0.25) emojiFactor = 0.8;

        let prob = profile.baseCTR * qualityMultiplier * resonanceMultiplier * relevanceMult
          * moodMult * fatigueMult * scrollMult * competitionMult * timeMult
          * skepticismMult * fatiguePenalty * emojiFactor * (options.thumbnailMult || 1);

        return Math.max(0.001, Math.min(0.15, prob));
      });

      // Viewer picks the title with highest appeal, but only if it passes threshold
      const totalProb = probs.reduce((s, p) => s + p, 0);
      const roll = rng();
      if (roll < totalProb) {
        // Weighted selection — higher prob = more likely to be picked
        let cumulative = 0;
        for (let k = 0; k < probs.length; k++) {
          cumulative += probs[k];
          if (roll < cumulative) {
            clicks[k]++;
            archetypeClicks[k][profile.archetype]++;
            break;
          }
        }
      }
    }
    if (onProgress) onProgress(0, end, profileCount);
    await new Promise(r => setTimeout(r, 0));
  }

  return titles.map((title, i) => ({
    title,
    clicks: clicks[i],
    impressions: profileCount,
    ctr: clicks[i] / profileCount,
    ctrLow: null,
    ctrHigh: null,
    signals: allSignals[i],
    archetypeBreakdown: ARCHETYPES.map(a => ({
      name: a.name,
      clicks: archetypeClicks[i][a.name],
      total: archetypeTotals[a.name],
      ctr: archetypeTotals[a.name] > 0 ? archetypeClicks[i][a.name] / archetypeTotals[a.name] : 0,
    })),
  }));
}

export { ARCHETYPES, analyzeTitle };
